import React from 'react';
import { Link } from 'react-router-dom';

import SubtleBadge from 'components/common/SubtleBadge';

import paths from 'routes/paths';
import { formatStatus } from 'utils/valueFormatters';
import { getPicklistBadgeColor } from 'constants/picklist';
import { getShipmentBadgeColor } from 'constants/shipment';
import { TIME_OPTION } from 'constants/time';

export const getPicklistListColumns = (tenant, warehouse) => [
  {
    accessorKey: 'picklist_code',
    header: 'Picklist Code',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { id, picklist_code } = original;

      return (
        <Link
          to={`${paths.picklists.details(tenant, warehouse, id)}?tab=shipments`}
        >
          {picklist_code}
        </Link>
      );
    },
  },
  {
    accessorKey: 'picklist_status',
    header: 'Status',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { picklist_status, picklist_status_label } = original;

      return (
        <SubtleBadge pill bg={getPicklistBadgeColor(picklist_status)}>
          {picklist_status_label}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'created',
    header: 'Created',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { created } = original;

      return (
        <span>
          {created
            ? new Date(created).toLocaleString('en-IN', TIME_OPTION)
            : '-'}
        </span>
      );
    },
  },
];

export const getCreatePicklistListColumns = (tenant, warehouse) => [
  {
    accessorKey: 'order',
    header: 'Shipment',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { order, shipment_code } = original;

      return (
        <div>
          <p className="d-flex align-items-center mb-2">
            <span className="fw-semibold me-1">Code:</span>
            <Link
              to={`${paths.orders.details(
                tenant,
                warehouse,
                order?.id,
              )}?tab=shipments`}
            >
              <strong>{shipment_code}</strong>
            </Link>
          </p>
          <p className="d-flex align-items-center mb-0">
            <span className="fw-semibold me-1">Order:</span>
            <Link
              to={`${paths.orders.details(
                tenant,
                warehouse,
                order?.id,
              )}?tab=order-items`}
            >
              <strong>{order.channel_order_id}</strong>
            </Link>
          </p>
        </div>
      );
    },
  },
  {
    accessorKey: 'total_quantity',
    header: 'Total Quantity',
    enableSorting: false,
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) => {
      return <span>{original?.total_quantity.toLocaleString()}</span>;
    },
  },
  {
    accessorKey: 'shipment_status',
    header: 'Shipment Status',
    enableSorting: false,
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => {
      const { shipment_status } = original;

      return (
        <SubtleBadge pill bg={getShipmentBadgeColor(shipment_status)}>
          {formatStatus(shipment_status)}
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'brand',
    header: 'Brand',
    enableSorting: false,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { brand } = original;

      return <span>{brand?.name || '-'}</span>;
    },
  },
  {
    accessorKey: 'product_summary',
    header: 'Products',
    enableSorting: false,
    meta: {
      valueType: 'string',
      minWidth: '300px',
      maxWidth: '300px',
    },
    cell: ({ row: { original } }) => {
      const { product_summary } = original;

      if (!product_summary?.length) return <span>-</span>;

      return (
        <div className="d-flex flex-column gap-2">
          {product_summary.map((product, idx) => (
            <div
              key={product?.product_sku}
              className={`pb-1 ${
                idx === product_summary.length - 1 ? '' : 'border-bottom'
              }`}
            >
              <div
                className="text-truncate"
                title={product?.product_name || '-'}
              >
                <strong>Name: </strong>
                {product?.product_name || '-'}
              </div>
              <div>
                <strong>SKU: </strong>
                {product?.product_sku || '-'}
              </div>
              <div>
                <strong>Quantity: </strong> {product?.quantity}
              </div>
            </div>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: 'created',
    header: 'Created',
    enableSorting: true,
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { created } = original;

      return (
        <span>{new Date(created).toLocaleString('en-IN', TIME_OPTION)}</span>
      );
    },
  },
];

export const getPicklistDetailShipmentColumns = (tenant, warehouse) => [
  {
    accessorKey: 'shipment_code',
    header: 'Shipment',
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => (
      <Link
        to={`${paths.orders.details(
          tenant,
          warehouse,
          original.order.id,
        )}?tab=shipments`}
      >
        {original.shipment_code}
      </Link>
    ),
  },
  {
    accessorKey: 'order.id',
    header: 'Sale Order Code',
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => (
      <Link
        to={`${paths.orders.details(
          tenant,
          warehouse,
          original.order.id,
        )}?tab=order-items`}
      >
        {original.order.channel_order_id}
      </Link>
    ),
  },
  {
    accessorKey: 'order.channel',
    header: 'Channel',
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => original.order?.channel.channel_type,
  },
  {
    accessorKey: 'shipment_status_label',
    header: 'Shipment Status',
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row: { original } }) => (
      <SubtleBadge bg={getShipmentBadgeColor(original.shipment_status)}>
        {original.shipment_status_label}
      </SubtleBadge>
    ),
  },
  {
    accessorKey: 'shipment_line_summary.total_quantity',
    header: 'Total Quantity',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) =>
      original.shipment_line_summary.total_quantity.toLocaleString() || 0,
  },
];

export const getPicklistDetailItemsColumns = (tenant, warehouse) => [
  {
    accessorKey: 'name',
    header: 'Item',
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => original.name,
  },
  {
    accessorKey: 'sku',
    header: 'SKU Code',
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => original.sku,
  },
  {
    accessorKey: 'barcode',
    header: 'Barcode',
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => original.barcode,
  },
  {
    accessorKey: 'shipment_line_summary.total_quantity',
    header: 'Total Quantity',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) =>
      original.shipment_line_summary.total_quantity.toLocaleString() || 0,
  },
  {
    accessorKey: 'shipment_line_summary.reject_not_found_quantity',
    header: 'Not Found Quantity',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) =>
      original.shipment_line_summary.reject_not_found_quantity.toLocaleString() ||
      0,
  },
  {
    accessorKey: 'shipment_line_summary.reject_damaged_quantity',
    header: 'Damaged Quantity',
    meta: {
      valueType: 'number',
    },
    cell: ({ row: { original } }) =>
      original.shipment_line_summary.reject_damaged_quantity.toLocaleString() ||
      0,
  },
];

export const getPicklistProcessShipmentColumns = (tenant, warehouse) => [
  {
    accessorKey: 'shipment_code',
    header: 'Shipment Code',
    meta: {
      valueType: 'string',
    },
    cell: ({ row }) => row.original.shipment_code,
  },
  {
    accessorKey: 'shipment_status_label',
    header: 'Status',
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row }) => (
      <SubtleBadge bg={getShipmentBadgeColor(row.original?.shipment_status)}>
        {row.original?.shipment_status_label}
      </SubtleBadge>
    ),
  },
  {
    accessorKey: 'order.id',
    header: 'Order',
    meta: {
      valueType: 'string',
    },
    cell: ({ row: { original } }) => {
      const { order } = original;

      return (
        <Link
          to={`${paths.orders.details(
            tenant,
            warehouse,
            order.id,
          )}?tab=shipments`}
        >
          {order.channel_order_id}
        </Link>
      );
    },
  },
  {
    accessorKey: 'order.channel.channel_type',
    header: 'Channel',
    meta: {
      headerProps: { className: 'text-center' },
      cellProps: { className: 'text-center' },
    },
    cell: ({ row }) => (
      <SubtleBadge bg="secondary">
        {row.original?.order?.channel?.channel_type || '-'}
      </SubtleBadge>
    ),
  },
  {
    accessorKey: 'shipment_line_summary.total_quantity',
    header: 'Total Quantity',
    meta: {
      valueType: 'number',
    },
    cell: ({ row }) => row.original?.shipment_line_summary?.total_quantity || 0,
  },
  {
    accessorKey: 'shipment_line_summary.dispatched_quantity',
    header: 'Picked Quantity',
    meta: {
      valueType: 'number',
    },
    cell: ({ row }) =>
      row.original?.shipment_line_summary?.picked_quantity || 0,
  },
  {
    accessorKey: 'shipment_line_summary.picking_quantity',
    header: 'Picking Quantity',
    meta: {
      valueType: 'number',
    },
    cell: ({ row }) =>
      row.original?.shipment_line_summary?.picking_quantity || 0,
  },
  {
    accessorKey: 'invoice_code',
    header: 'Invoice Code',
    meta: {
      valueType: 'string',
    },
    cell: ({ row }) => row.original?.invoice_code || '-',
  },
];

export const getPicklistProcessItemsColumns = (tenant, warehouse) => [
  {
    accessorKey: 'name',
    header: 'Item',
    meta: {
      valueType: 'string',
    },
    cell: ({ row }) => row.original.name,
  },
  {
    accessorKey: 'barcode',
    header: 'Barcode',
    meta: {
      valueType: 'string',
    },
    cell: ({ row }) => row.original.barcode,
  },
  {
    accessorKey: 'shipment_line_summary.total_quantity',
    header: 'Total Quantity',
    meta: {
      valueType: 'number',
    },
    cell: ({ row }) => row.original?.shipment_line_summary?.total_quantity || 0,
  },
  {
    accessorKey: 'shipment_line_summary.created_quantity',
    header: 'Created Quantity',
    meta: {
      valueType: 'number',
    },
    cell: ({ row }) =>
      row.original?.shipment_line_summary?.created_quantity || 0,
  },
  {
    accessorKey: 'shipment_line_summary.picking_quantity',
    header: 'Picking Quantity',
    meta: {
      valueType: 'number',
    },
    cell: ({ row }) =>
      row.original?.shipment_line_summary?.picking_quantity || 0,
  },
  {
    accessorKey: 'shipment_line_summary.picked_quantity',
    header: 'Picked Quantity',
    meta: {
      valueType: 'number',
    },
    cell: ({ row }) =>
      row.original?.shipment_line_summary?.picked_quantity || 0,
  },
  {
    accessorKey: 'shipment_line_summary.damaged_quantity',
    header: 'Damaged Quantity',
    meta: {
      valueType: 'number',
    },
    cell: ({ row }) =>
      row.original?.shipment_line_summary?.damaged_quantity || 0,
  },
];
